import { TFunction } from 'i18next';
import { BillingSourceEnum, ContractMissingEnum, ContractStatusFilterEnum } from '../../../../common/enums/invoicing.enum';
import { ChildContractDto } from '../../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../../utilities/helper/money.helper';

/** The order things are listed in whenever more than one is missing. */
const MISSING_ORDER: ContractMissingEnum[] = [
  ContractMissingEnum.MEMBERSHIP,
  ContractMissingEnum.CONTRACT_NUMBER,
  ContractMissingEnum.PAYER,
  ContractMissingEnum.PRICE
];

/** "broj ugovora, platilac" - what a child lacks, for "Fali …" sentences. */
export const missingList = (t: TFunction, missing: ContractMissingEnum[]): string =>
  MISSING_ORDER.filter(key => missing.includes(key))
    .map(key => t(`contracts.missingShort.${key}`))
    .join(', ');

/** The status cell and the Billing tab say this, so the wording is written once. */
export const statusText = (t: TFunction, contract: Pick<ChildContractDto, 'isReadyForInvoicing' | 'missing' | 'nextInvoiceNumber'>): string =>
  contract.isReadyForInvoicing
    ? t('contracts.status.ready', { number: contract.nextInvoiceNumber ?? '' })
    : t('contracts.status.missing', { what: missingList(t, contract.missing) });

/** "Grupa · Lokacija", or the fact that there is no group. */
export const placementText = (t: TFunction, contract: Pick<ChildContractDto, 'groupName' | 'locationName'>): string =>
  contract.groupName ? [contract.groupName, contract.locationName].filter(Boolean).join(' · ') : t('contracts.notEnrolled');

/**
 * The "What is charged" cell: where the price comes from, and under it either the amount
 * or what to do next. Attendance lines have no monthly figure, so they are mentioned
 * rather than left out of a number that would then look complete.
 */
export const pricingText = (
  t: TFunction,
  contract: Pick<ChildContractDto, 'billingSource' | 'billingSourceName' | 'monthlyFixedNet' | 'hasAttendanceItems' | 'membershipId'>
): { title: string; detail: string; missing: boolean } => {
  if (contract.billingSource === BillingSourceEnum.NONE) {
    return contract.membershipId
      ? { title: t('contracts.pricing.groupMissing'), detail: t('contracts.pricing.groupMissingNext'), missing: true }
      : { title: t('contracts.pricing.none'), detail: t('contracts.pricing.noneNext'), missing: true };
  }
  const title =
    contract.billingSource === BillingSourceEnum.GROUP_SCHEME
      ? t('contracts.pricing.group')
      : contract.billingSourceName
        ? t('contracts.pricing.ownNamed', { name: contract.billingSourceName })
        : t('contracts.pricing.own');
  const amount = t('contracts.pricing.monthly', { amount: formatMoney(contract.monthlyFixedNet ?? 0, { withCurrency: true }) });
  return {
    title,
    detail: contract.hasAttendanceItems ? `${amount} ${t('contracts.pricing.plusAttendance')}` : amount,
    missing: false
  };
};

/** Invoice numbers are the contract number plus a two-digit sequence: UG-2026/019-01. */
export const invoiceNumber = (contractNumber: string, seq: number): string => `${contractNumber}-${String(seq).padStart(2, '0')}`;

/** "UG-1-01", "UG-1-01 i -02", "UG-1-01 do -05": the invoices a new number would not touch. */
export const issuedRange = (t: TFunction, contractNumber: string, lastInvoiceSeq: number): string => {
  const first = invoiceNumber(contractNumber, 1);
  const last = String(lastInvoiceSeq).padStart(2, '0');
  if (lastInvoiceSeq <= 1) return t('contracts.drawer.number.issuedOne', { first });
  if (lastInvoiceSeq === 2) return t('contracts.drawer.number.issuedTwo', { first, last });
  return t('contracts.drawer.number.issuedMany', { first, last });
};

const STATUS_VALUES = Object.values(ContractStatusFilterEnum) as string[];

/** `?status=` as the filter it names, or none for anything else - a typo shows every child. */
export const parseStatusFilter = (value: string | null): ContractStatusFilterEnum | undefined =>
  value && STATUS_VALUES.includes(value) ? (value as ContractStatusFilterEnum) : undefined;
